import {
  Controller, FormProvider,
} from 'react-hook-form';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from '../../common/components/input/TextInput';
import FromHeading from './FormHeading';
import FormParagraph from './FormParagraph';
import SelectInput from '../../common/components/input/SelectInput';
import RadioInput from '../../common/components/input/RadioInput';
import DateInput from '../../common/components/input/DateInput';
import FormTerms from './FormTerms';
import Review from './Review';

import Checkbox from '../../common/components/input/Checkbox';
import PaymentBlock from './PaymentBlock';
import AddressBlock from './AddressBlock';
import AutoCompleteInput from '../../common/components/input/AutoComplete';
import {
  actions, getQuote,
} from './QuoteSlice';
import CompanyLookup from '../../common/components/input/CompanyLookup';

import AddressSearchInput from '../../common/components/input/AddressSearchInput';
import HiddenInput from '../../common/components/input/HiddenInput';
import getValidation from '../../validations';
import KciMessage from './KciMessage';
import TextLabel from '../../common/components/label/TextLabel';
import Utils from '../../common/helpers/Utils';
import WatchTriggers from './data/WatchTriggers';
import {
  ErnNumberInterface, Field, WatchTriggerInterface,
} from '../../types';
import ErnNumberInput from '../../common/components/input/ErnNumberInput';
import useWatchTriggers from '../../common/hooks/useWatchTriggers';
import useQuoteJourney from '../../common/hooks/useQuoteJourney';
import { DataActions, DataCategories, DataEvents } from '../../types/enum';
import TextComponent from '../../common/components/copy/TextComponent';
import SmallComponent from '../../common/components/copy/SmallCopyCompontent';

function Form() {
  const dispatch = useDispatch();
  const quoteData = useSelector(getQuote);
  const { innerStep, step: outerStep } = quoteData.step;

  const {
    nextStep, previousStep, methods, fields, goToStart, submitToServer,
    append,
  } = useQuoteJourney();

  const [loadedTriggers, addToTriggers, checkForTriggers] = useWatchTriggers();

  const stepObject = quoteData.steps[quoteData.step.step];
  const innerStepObject = stepObject.steps[quoteData.step.innerStep];
  const { isValid, errors } = methods.formState;
  const onSubmit = (data: any, e: any) => data;
  return (
    <div className={`${innerStepObject.theme}`}>
      {quoteData.step.step === 2
        && quoteData.quote.json?.message
        && (
        <Review type="summary" />
        )}
      <section className="container p-3">

        {quoteData.step.step === 0 && quoteData.step.innerStep > 0 && (
        <button
          type="button"
          onClick={previousStep}
          className="btn position-sticky btn-outline-dark btn-back float-end"
        >
          &lt; Go back
        </button>
        )}
        <div className={`quote container my-3 quote--${innerStepObject.type}`}>
          <KciMessage />
          {(quoteData.isLoading || quoteData.isFetching) && (
            <div className="modal-backdrop fade show">
              <div className="d-flex flex-column h-100 align-items-center justify-content-center">
                <div className="spinner-border" style={{ width: '3rem', height: '3rem' }} role="status" />
                <div className="loading-copy text-white">
                  {quoteData.loadingText
                    && <span className="" dangerouslySetInnerHTML={{ __html: quoteData.loadingText }} />}
                </div>
              </div>
            </div>
          )}
          <FormProvider {...methods}>
            {innerStepObject.type === 'form'
              ? (
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {(fields).map((field: any, index: number) => {
                    // When we render lets check for triggers too
                    const validationRules = getValidation(field);
                    const validation = {
                      ...validationRules,
                      // disabled: field.disabled === 'disabled',
                      onChange: async (e: any) => {
                        // add control to email inputs
                        if (field.vtype === 'email') {
                          methods.setValue(`quote.${index}.value`, e.currentTarget.value.trim());
                        } else if (field.type === 'date') {
                          const formattedDate = e.target ? new Date(e.target.value) : new Date();
                          const stringDate = field.vtype === 'inception' ? formattedDate.toISOString().split('T')[0] : formattedDate.toLocaleDateString('en-GB');
                          methods.setValue(`quote.${index}.value`, stringDate);
                          checkForTriggers(
                            field,
                            formattedDate,
                            index,
                          );
                        }
                        dispatch(actions.updateQuote(methods.getValues()));
                        if (Object.prototype.hasOwnProperty.call(e, 'currentTarget')) {
                          if (e.currentTarget.value || e.currentTarget.checked) {
                            checkForTriggers(
                              field,
                              e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value,
                              index,
                            );
                          }
                        }
                        // Reset any messaging
                        dispatch(actions.setMessage(''));
                      },
                    };
                    switch (field.type) {
                      case 'select':
                        return (
                          <SelectInput
                            inputRef={{
                              ...methods.register(`quote.${index}.value`, validation),
                            }}
                            key={field.id}
                            field={field}
                            error={errors.quote?.[index]?.value.message}
                          />
                        );
                      case 'p':
                        return <FormParagraph key={index} field={field} />;
                      case 'terms':
                        return <FormTerms key={index} field={field} />;
                      case 'address':
                        return <AddressBlock key={index} field={field} />;
                      case 'breakdown':
                        return <PaymentBlock key={index} />;
                      case 'radio':
                        return (
                          <RadioInput
                            inputRef={methods.register(`quote.${index}.value`, validation)}
                            key={field.id}
                            field={field}
                            error={errors.quote?.[index]?.value.message}
                          />
                        );
                      case 'date':
                        return (
                          <Controller
                            key={field.id}
                            rules={validation}
                            name={`quote.${index}.value`}
                            control={methods.control}
                            render={({
                              field: {
                                onChange, onBlur, value, name, ref,
                              },
                            }) => (
                              <DateInput
                                onChange={onChange}
                                selected={value}
                                field={field}
                                error={errors.quote?.[index]?.value.message}
                              />
                            )}
                          />
                        );
                      case 'h3':
                        return <FromHeading key={index} field={field} />;
                      case 'checkbox':
                        return (
                          <Checkbox
                            inputRef={methods.register(`quote.${index}.value`, validation)}
                            key={field.id}
                            field={field}
                            error={errors.quote?.[index]?.value.message}
                          />
                        );
                      case 'company_reg_lookup':
                        return (
                          <CompanyLookup
                            inputRef={methods.register(`quote.${index}.value`, validation)}
                            key={field.id}
                            field={field}
                            error={errors.quote?.[index]?.value.message}
                            setError={(errorString: string) => methods.setError(`quote.${index}.value`, {
                              type: 'custom',
                              message: errorString,
                            })}
                          />
                        );
                      case 'ern':
                        return (
                          <Controller
                            key={field.id}
                            rules={validationRules}
                            name={`quote.${index}.value`}
                            control={methods.control}
                            render={({
                              field: {
                                onChange, onBlur, value, name, ref,
                              },
                            }) => (
                              <ErnNumberInput
                                inputRef={methods.register(`quote.${index}.value`, validation)}
                                key={field.id}
                                onChange={(ernValue: ErnNumberInterface) => {
                                  methods.setValue(`quote.${index}.value`, ernValue);
                                  onChange(ernValue);
                                }}
                                field={field}
                                error={errors.quote?.[index]?.value.message}
                              />
                            )}
                          />
                        );
                      case 'text':
                        return (
                          <TextInput
                            inputRef={methods.register(
                              `quote.${index}.value`,
                              validation,
                            )}
                            key={field.id}
                            field={field}
                            error={errors.quote?.[index]?.value.message}
                          />
                        );
                      case 'hidden':
                        return (
                          <HiddenInput
                            inputRef={methods.register(`quote.${index}.value`, validation)}
                            key={field.id}
                            field={field}
                          />
                        );
                      case 'postcode':
                        return (
                          <AddressSearchInput
                            inputRef={methods.register(`quote.${index}.value`, validation)}
                            key={field.id}
                            field={field}
                            value={methods.getValues(`quote.${index}.value`)}
                            error={errors.quote?.[index]?.value.message}
                          />
                        );
                      case 'button':
                      case 'submit':

                        return (
                          <div key={field.id}>
                            {quoteData.step.step === 3 && field.formId.indexOf('submit') > -1
                              && (
                              <button onClick={nextStep} type="submit">
                                {field.value}
                              </button>
                              )}
                          </div>
                        );
                        break;
                      case 'autocomplete':
                        return (
                          <Controller
                            key={field.id}
                            rules={validation}
                            name={`quote.${index}.value`}
                            control={methods.control}
                            render={({
                              field: {
                                onChange,
                              },
                            }) => (
                              <AutoCompleteInput
                                onChange={(e: any) => {
                                  methods.setValue(`quote.${index}.value`, e);
                                  onChange(e);
                                  if (field.formId.indexOf('extra') > -1) {
                                    append({
                                      label: '',
                                      id: 'reloading_with_found_via_partner',
                                      formId: 'reloading_with_found_via_partner',
                                      group: '',
                                      text: '',
                                      type: 'hidden',
                                      vtype: 'text',
                                      value: e,
                                    });
                                    dispatch(actions.setIsLoading(true));
                                    dispatch(actions.setLoadingText('We need to reload the page to add your partner information.'));
                                    setTimeout(() => {
                                      submitToServer(Utils.prepareValues(methods.getValues()));
                                    }, 500);
                                  }
                                  checkForTriggers(
                                    field,
                                    e,
                                    index,
                                  );
                                }}
                                name={`quote.${index}.value`}
                                error={errors.quote?.[index]?.value.message}
                                field={field}
                              />
                            )}
                          />
                        );
                        return <TextComponent key={field.id} field={field} />;
                      case 'div':
                      case 'component':
                      case 'section':
                      case 'label':
                        return field.text && <SmallComponent key={field.id} field={field} />;
                      default:
                        if (field.label_style) { // Assume this is a label
                          return <SmallComponent key={field.id} field={field} />;
                        }
                        return '';
                    }
                  })}
                  {quoteData.step.step !== 3
                    && (
                    <button onClick={nextStep} type="submit">
                      Next Steps
                    </button>
                    )}
                </form>
              )
              : <Review />}
          </FormProvider>
        </div>
      </section>
      {quoteData.step.step === 2
        && !quoteData.quote.json?.message
        && (
        <Review type="summary" />
        )}
      {innerStepObject.type === 'review'
                && (
                <footer className="footer footer--form">
                  <div className="container">
                    <div className="row">
                      <div className="col-12 mt-3 mt-md-0 col-md-6">
                        <span className="d-block text--green">
                          All cover is underwritten by
                          {' '}
                          <span dangerouslySetInnerHTML={
                            { __html: quoteData.quote.json.underwriteNotice }
                          }
                          />
                        </span>
                        <small>
                          *Indicative: Your monthly amount may differ by a few pence,
                          you can refer to your welcome pack from Close Brothers Premium
                          Finance to review your full breakdown and payment schedule once purchased.
                          Please note that the overall cost of your policy is greater if you pay
                          via monthly instalments.
                        </small>
                      </div>
                      <div className="col-12 mt-3 mt-md-0 col-md-6">
                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            onClick={(event: any) => {
                              Utils.sendDataLayerEvent({
                                event:
                                  DataEvents[DataEvents.analyticsEvent],
                                eventCategory:
                                  DataCategories[DataCategories['Quote Journey']],
                                eventAction:
                                  DataActions[DataActions['Change options']],
                                stepNumber: outerStep + 1,
                                stepName: quoteData.steps[(outerStep)].steps[innerStep].title,
                                substage: innerStep + 1,
                              });
                              if (outerStep > 2) {
                                window.location.assign(`/buy?ptc=${quoteData.ptc}`);
                              } else {
                                goToStart(event);
                              }
                            }}
                            className="btn kb-btn--outline kb-btn-form--footer btn-tel align-self-end py-lg-3 py-1 px-4"
                          >
                            Change options
                          </button>
                          <button type="button" onClick={nextStep} className="btn ms-4 px-5 kb-btn--form-footer btn--green align-self-start py-lg-3 py-1 px-4">
                            Got it, thanks.
                          </button>
                        </div>
                      </div>
                    </div>
                    <hr className="mt-5 mt-lg-3" />

                  </div>
                </footer>
                )}

    </div>
  );
}

export default Form;
